import type { components } from "@/api/types";
import type { IncidentMap } from "@/api/queries/incidents";
import { projectToFraction } from "@/lib/map-projection";

type Position = components["schemas"]["PositionRead"];

type Dot = { id: string; x: number; y: number };

/**
 * Static incident map snapshot with one frozen dot per firefighter at their
 * last-known position. This is the after-action counterpart to the IC app's live
 * map: same tight, georeferenced satellite view, but the dots never move — they
 * mark where each responder ended up. Coordinates are projected onto the square
 * snapshot via Web Mercator (the snapshot's `bounds`), matching the iOS
 * projection so the two views agree. With no `bounds` (incident has no known
 * location) we fall back to the bare image so the card still renders.
 */
export function IncidentMapSnapshot({
  map,
  positions,
}: {
  map: IncidentMap;
  positions: Position[];
}) {
  const bounds = map.bounds;
  const dots: Dot[] = bounds
    ? positions.flatMap((p) => {
        const f = projectToFraction(
          { latitude: Number(p.latitude), longitude: Number(p.longitude) },
          bounds,
        );
        return f ? [{ id: p.user_id, x: f.x, y: f.y }] : [];
      })
    : [];

  return (
    <div className="space-y-2">
      <div className="relative w-full overflow-hidden rounded-md">
        <img
          src={map.url}
          alt="Incident location with firefighter positions"
          className="aspect-square w-full object-cover"
        />
        {dots.map((d) => (
          <span
            key={d.id}
            className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow"
            style={{ left: `${d.x * 100}%`, top: `${d.y * 100}%` }}
            title={`Firefighter ${d.id.slice(0, 8)}`}
            data-testid="firefighter-dot"
          />
        ))}
      </div>
      {positions.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {positions.length} firefighter
          {positions.length === 1 ? "" : "s"} · last-known position
          {positions.length === 1 ? "" : "s"} (static)
        </p>
      ) : bounds ? (
        <p className="text-xs text-muted-foreground">
          No firefighter positions recorded for this incident.
        </p>
      ) : null}
    </div>
  );
}
